// src/pages/Home.js
// Apple Dark Mode Style - Dynamic Landing with Wizard
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Home.css';
import API_URL from '../config';

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [groupName, setGroupName] = useState('');
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [meetingDay, setMeetingDay] = useState('Fredag');
  const [meetingTime, setMeetingTime] = useState('18:00');
  const [isCreating, setIsCreating] = useState(false);
  const [createdGroup, setCreatedGroup] = useState(null);
  
  const emailInputRef = useRef(null);

  // Email validation
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const addEmail = (email) => {
    email = email.trim().toLowerCase();
    if (!email || !isValidEmail(email) || emails.includes(email)) return false;
    setEmails([...emails, email]);
    return true;
  };

  const removeEmail = (emailToRemove) => {
    setEmails(emails.filter(e => e !== emailToRemove));
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (addEmail(emailInput)) {
        setEmailInput('');
      }
    }
    
    // Backspace to remove last email
    if (e.key === 'Backspace' && !emailInput && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  const handleEmailBlur = () => {
    if (emailInput && addEmail(emailInput)) {
      setEmailInput('');
    }
  };

  // Navigation
  const nextStep = () => {
    if (currentStep === 1 && !groupName.trim()) {
      document.getElementById('group-name-input')?.focus();
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Create group (stores in localStorage for after registration)
  const handleCreateGroup = async () => {
    if (isLoggedIn) {
      // Create group directly
      setIsCreating(true);
      try {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        const response = await fetch(`${API_URL}/api/groups`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            groupName: groupName.trim(),
            meetingFrequency: { dayOfWeek: meetingDay, time: meetingTime },
            frequency: frequency === 'weekly' ? 'Varje vecka' : 'Varannan vecka',
            participants: emails.map(email => ({ email, name: email.split('@')[0] })),
            threshold: Math.ceil(emails.length * 0.5) || 1,
            timezone: userTimezone,
            location: '',
            invitationAdvance: 7,
            reminderAdvance: 3,
            decisionTime: '18:00'
          })
        });

        if (response.ok) {
          const newGroup = await response.json();
          setCreatedGroup(newGroup);
          setCurrentStep(4);
          window.dispatchEvent(new Event('groupsUpdated'));
        } else {
          const error = await response.json();
          alert('Kunde inte skapa grupp: ' + error.message);
        }
      } catch (error) {
        console.error('Error creating group:', error);
        alert('Något gick fel');
      } finally {
        setIsCreating(false);
      }
    } else {
      // Store group data for after registration
      const groupData = {
        groupName: groupName.trim(),
        emails,
        frequency,
        meetingDay,
        meetingTime
      };
      localStorage.setItem('pendingGroup', JSON.stringify(groupData));
      setCreatedGroup({ groupName: groupData.groupName });
      setCurrentStep(4);
    }
  };

  const handleGoToGroup = () => {
    if (createdGroup?._id) {
      // Öppna schema-sektionen automatiskt efter Wizard
      navigate(`/groups/${createdGroup._id}`, { 
        state: { openScheduleSection: true } 
      });
    } else {
      navigate('/dashboard');
    }
  };

  const handleRegister = () => {
    navigate('/signup');
  };

  // Days and times options
  const days = [
    { value: 'Måndag', label: t('days.monday') },
    { value: 'Tisdag', label: t('days.tuesday') },
    { value: 'Onsdag', label: t('days.wednesday') },
    { value: 'Torsdag', label: t('days.thursday') },
    { value: 'Fredag', label: t('days.friday') },
    { value: 'Lördag', label: t('days.saturday') },
    { value: 'Söndag', label: t('days.sunday') }
  ];

  const times = [];
  for (let h = 7; h <= 22; h++) {
    times.push(`${h.toString().padStart(2, '0')}:00`);
    if (h < 22) times.push(`${h.toString().padStart(2, '0')}:30`);
  }

  return (
    <div className="home">
      {/* Hero Section with Wizard */}
      <section className="home-hero">
        <div className="home-hero-content">
          <div className="wizard-card">
            {/* Header */}
            <div className="wizard-header">
              <p className="wizard-tagline">{t('home.wizard.tagline')}</p>
              <h1 className="wizard-title">
                {currentStep === 4 
                  ? t('home.wizard.successTitle')
                  : t('home.wizard.title')
                }
              </h1>
              {currentStep !== 4 && (
                <p className="wizard-subtitle">
                  {t('home.wizard.subtitle')}
                </p>
              )}
            </div>

            {/* Progress Indicator */}
            {currentStep !== 4 && (
              <div className="wizard-progress">
                {[1, 2, 3].map(step => (
                  <div 
                    key={step}
                    className={`wizard-progress-step ${
                      step < currentStep ? 'completed' : 
                      step === currentStep ? 'active' : ''
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Step 1: Group Name */}
            {currentStep === 1 && (
              <div className="wizard-step animate-fade-in">
                <div className="form-group">
                  <label className="form-label">{t('home.wizard.groupName')}</label>
                  <input
                    id="group-name-input"
                    type="text"
                    className="form-input"
                    placeholder={t('home.wizard.groupNamePlaceholder')}
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && nextStep()}
                    autoFocus
                  />
                  <p className="form-hint">{t('home.wizard.groupNameHint')}</p>
                </div>

                <div className="wizard-footer">
                  <button 
                    className="btn btn-primary btn-large btn-full"
                    onClick={nextStep}
                    disabled={!groupName.trim()}
                  >
                    {t('home.wizard.continue')}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Add Participants */}
            {currentStep === 2 && (
              <div className="wizard-step animate-fade-in">
                <div className="form-group">
                  <label className="form-label">{t('home.wizard.addParticipants')}</label>
                  <div 
                    className="tags-input-wrapper"
                    onClick={() => emailInputRef.current?.focus()}
                  >
                    <div className="tags-container">
                      {emails.map(email => (
                        <div key={email} className="tag">
                          {email}
                          <button 
                            className="tag-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeEmail(email);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <input
                        ref={emailInputRef}
                        type="email"
                        className="tags-input"
                        placeholder={emails.length === 0 ? t('home.wizard.emailPlaceholder') : ''}
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={handleEmailKeyDown}
                        onBlur={handleEmailBlur}
                      />
                    </div>
                  </div>
                  <p className="form-hint">{t('home.wizard.emailHint')}</p>
                </div>

                {emails.length > 0 && (
                  <div className="participant-count">
                    <span className="participant-count-number">{emails.length}</span>
                    <span className="participant-count-text">
                      {t('home.wizard.participantsAdded')}
                    </span>
                  </div>
                )}

                <div className="wizard-footer">
                  <button className="btn btn-secondary btn-large" onClick={prevStep}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    {t('home.wizard.back')}
                  </button>
                  <button className="btn btn-primary btn-large" onClick={nextStep}>
                    {t('home.wizard.continue')}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Meeting Schedule */}
            {currentStep === 3 && (
              <div className="wizard-step animate-fade-in">
                <div className="form-group">
                  <label className="form-label">{t('home.wizard.frequency')}</label>
                  <div className="meeting-options">
                    <div className="meeting-option">
                      <input 
                        type="radio" 
                        name="frequency" 
                        id="weekly" 
                        value="weekly"
                        checked={frequency === 'weekly'}
                        onChange={(e) => setFrequency(e.target.value)}
                      />
                      <label className="meeting-option-label" htmlFor="weekly">
                        <span className="meeting-option-icon">📅</span>
                        <span className="meeting-option-text">{t('home.wizard.weekly')}</span>
                      </label>
                    </div>
                    <div className="meeting-option">
                      <input 
                        type="radio" 
                        name="frequency" 
                        id="biweekly" 
                        value="biweekly"
                        checked={frequency === 'biweekly'}
                        onChange={(e) => setFrequency(e.target.value)}
                      />
                      <label className="meeting-option-label" htmlFor="biweekly">
                        <span className="meeting-option-icon">📆</span>
                        <span className="meeting-option-text">{t('home.wizard.biweekly')}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">{t('home.wizard.when')}</label>
                  <div className="time-selectors">
                    <div className="select-wrapper">
                      <select 
                        className="form-select"
                        value={meetingDay}
                        onChange={(e) => setMeetingDay(e.target.value)}
                      >
                        {days.map(day => (
                          <option key={day.value} value={day.value}>{day.label}</option>
                        ))}
                      </select>
                      <span className="select-arrow">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 9l6 6 6-6"/>
                        </svg>
                      </span>
                    </div>
                    <div className="select-wrapper">
                      <select 
                        className="form-select"
                        value={meetingTime}
                        onChange={(e) => setMeetingTime(e.target.value)}
                      >
                        {times.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                      <span className="select-arrow">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 9l6 6 6-6"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="wizard-footer">
                  <button className="btn btn-secondary btn-large" onClick={prevStep}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    {t('home.wizard.back')}
                  </button>
                  <button 
                    className="btn btn-primary btn-large"
                    onClick={handleCreateGroup}
                    disabled={isCreating}
                  >
                    {isCreating ? t('home.wizard.creating') : t('home.wizard.createGroup')}
                    {!isCreating && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12l5 5L20 7"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Success */}
            {currentStep === 4 && (
              <div className="wizard-step animate-fade-in">
                <div className="wizard-success">
                  <div className="success-icon">✓</div>
                  <p className="wizard-subtitle" style={{ marginBottom: 'var(--space-lg)' }}>
                    <strong>{createdGroup?.groupName || groupName}</strong> {t('home.wizard.isReady')}
                    <br />
                    {isLoggedIn 
                      ? t('home.wizard.successLoggedIn')
                      : t('home.wizard.successLoggedOut')
                    }
                  </p>

                  <div className="wizard-footer" style={{ flexDirection: 'column' }}>
                    {isLoggedIn ? (
                      <button 
                        className="btn btn-primary btn-large btn-full"
                        onClick={handleGoToGroup}
                      >
                        {t('home.wizard.goToGroup')}
                      </button>
                    ) : (
                      <>
                        <button 
                          className="btn btn-primary btn-large btn-full"
                          onClick={handleRegister}
                        >
                          {t('home.wizard.registerAndActivate')}
                        </button>
                        <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: 'var(--space-md)', textAlign: 'center' }}>
                          {t('home.wizard.alreadyAccount')}{' '}
                          <a href="/login" style={{ color: 'var(--accent)' }}>{t('nav.login')}</a>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="home-features">
        <div className="container">
          <div className="features-header">
            <h2 className="features-title">{t('home.features.sectionTitle')}</h2>
            <p className="features-subtitle">
              {t('home.features.sectionSubtitle')}
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📧</div>
              <h3 className="feature-title">{t('home.features.invitations.title')}</h3>
              <p className="feature-description">
                {t('home.features.invitations.description')}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🔔</div>
              <h3 className="feature-title">{t('home.features.reminders.title')}</h3>
              <p className="feature-description">
                {t('home.features.reminders.description')}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3 className="feature-title">{t('home.features.decisions.title')}</h3>
              <p className="feature-description">
                {t('home.features.decisions.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <p className="footer-text">© 2026 Moricome. Made with ♥ in Stockholm.</p>
        <div className="footer-links">
          <a href="/privacy" className="footer-link">{t('footer.privacy')}</a>
          <a href="/terms" className="footer-link">{t('footer.terms')}</a>
          <a href="/kontakt" className="footer-link">{t('footer.contact')}</a>
        </div>
      </footer>
    </div>
  );
}

export default Home;
